import { Injectable, inject } from '@angular/core';
import { JsonTypes } from 'typedjson';

import { Constructor, isEmptyString, isNil, Nillable } from '@web/utils';
import { CollectionRequest } from '../requests/collection.request';
import { CollectionResponse } from '../responses/collection.response';
import { JsonTransformerService } from '../../json/services/json-transformer.service';
import { CollectionFilterRequest } from '../requests/collection-filter.request';
import { CollectionSortRequest } from '../requests/collection-sort.request';

@Injectable({
  providedIn: 'root',
})
export class HttpHelperService {
  private readonly jsonTransformer = inject(JsonTransformerService);

  public requestToBody<Request>(
    request: Request,
    type: Constructor<Request>,
  ): JsonTypes {
    return this.jsonTransformer.toJson(request, type);
  }

  public requestToQueryParams<Request>(
    request: Request,
    requestType: Constructor<Request>,
  ): object {
    const plainObject = this.preparePlainObject(request, requestType) as object;

    return plainObject ?? {};
  }

  public collectionRequestToQueryParams(request: CollectionRequest): object {
    const { filter, page, sort, pageSize, all, query } = request;

    return {
      filter: this.preparePlainArray(filter, CollectionFilterRequest),
      sort: this.preparePlainArray(sort, CollectionSortRequest),
      query: this.preparePlainString(query),
      page,
      all,
      pageSize,
    };
  }

  public emptyBody(): Record<string, never> {
    return {};
  }

  public bodyToResponse<Response>(
    type: Constructor<Response>,
  ): (json: JsonTypes) => Response {
    return (json: JsonTypes) => this.jsonTransformer.toObject(json, type);
  }

  public bodyToCollectionResponse<CollectionItemResponse>(
    collectionItemResponseType: Constructor<CollectionItemResponse>,
  ): (json: JsonTypes) => CollectionResponse<CollectionItemResponse> {
    return (json: JsonTypes) => {
      const object = this.jsonTransformer.toObject(json, CollectionResponse);

      return new CollectionResponse<CollectionItemResponse>(
        object.page,
        object.pages,
        object.pageSize,
        object.total,
        this.jsonTransformer.toArray(object.items, collectionItemResponseType),
      );
    };
  }

  private preparePlainObject<ObjectType>(
    object: Nillable<ObjectType>,
    requestType: Constructor<ObjectType>,
  ): Nillable<JsonTypes> {
    if (isNil(object)) {
      return object;
    }

    return this.jsonTransformer.toJson(object, requestType);
  }

  private preparePlainArray<ObjectType>(
    object: Nillable<ObjectType[]>,
    requestType: Constructor<ObjectType>,
  ): Nillable<JsonTypes[]> {
    if (isNil(object)) {
      return object;
    }

    return this.jsonTransformer.toJsonArray(object, requestType);
  }

  private preparePlainString(query: Nillable<string>): Nillable<string> {
    if (isNil(query)) {
      return query;
    }

    if (isEmptyString(query)) {
      return null;
    }

    return query;
  }
}
